
import { useState, useRef } from 'react';
import { Upload, FileText, Play, Check, X } from 'lucide-react';

interface Difference {
  id: string;
  type: 'critical' | 'semantic' | 'format' | 'addition';
  summary: string;
  position: number;
  leftText: string;
  rightText: string;
  status: 'pending' | 'accepted' | 'rejected';
  leftHighlight?: string; // 左侧需要高亮的关键词
  rightHighlight?: string; // 右侧需要高亮的关键词
}

const ContractComparePage = () => {
  // 房屋租赁合同A（原合同）
  const defaultContractA = `房屋租赁合同

甲方（出租方）：张三
身份证号：110101199001011234
联系电话：13800138000
地址：北京市朝阳区建国路88号

乙方（承租方）：李四
身份证号：110101199002022345
联系电话：13900139000
地址：北京市海淀区中关村大街1号

根据《中华人民共和国合同法》及相关法律法规，甲乙双方在平等、自愿、协商一致的基础上，就房屋租赁事宜达成如下协议：

第一条 租赁房屋基本情况
甲方将其所有的位于北京市朝阳区望京街道花家地小区15号楼3单元502室的房屋（建筑面积85平方米，使用面积约75平方米）出租给乙方使用。房屋现状良好，家具家电齐全。

第二条 租赁期限
租赁期限为12个月，自2024年3月1日起至2025年2月28日止。租赁期满，如需续租，应提前30天协商。

第三条 租金及支付方式
月租金为人民币5000元整（¥5,000），每月1日前支付当月租金。首次支付时需一次性支付首月租金及两个月押金，共计人民币15000元整。

第四条 房屋用途
乙方承诺仅将房屋用于居住，不得擅自改变房屋用途，不得进行违法活动。

第五条 房屋维修
房屋正常维修由甲方负责，因乙方使用不当造成的损坏由乙方承担维修费用。

第六条 违约责任
如甲方违约，应双倍返还押金；如乙方违约，甲方有权没收押金并要求乙方立即搬离。

第七条 其他约定
1. 乙方不得私自转租房屋
2. 水电燃气费用由乙方承担
3. 物业费由甲方承担

第八条 争议解决
本合同履行过程中发生争议，双方应协商解决；协商不成的，可向房屋所在地人民法院起诉。

第九条 合同生效
本合同一式两份，甲乙双方各执一份，自双方签字之日起生效。

甲方签字：_________________ 日期：2024年2月25日
乙方签字：_________________ 日期：2024年2月25日`;

  // 房屋租赁合同B（修改后合同）
  const defaultContractB = `房屋租赁合同

甲方（出租方）：张三
身份证号：110101199001011234
联系电话：13800138000
地址：北京市朝阳区建国路88号

乙方（承租方）：李四
身份证号：110101199002022345
联系电话：13900139000
地址：北京市海淀区中关村大街1号

根据《中华人民共和国民法典》及相关法律法规，甲乙双方在平等、自愿、协商一致的基础上，就房屋租赁事宜达成如下协议：

第一条 租赁房屋基本情况
甲方将其所有的位于北京市朝阳区望京街道花家地小区15号楼3单元502室的房屋（建筑面积85平方米，使用面积约75平方米）出租给乙方使用。房屋现状良好，家具家电齐全。

第二条 租赁期限
租赁期限为18个月，自2024年3月1日起至2025年8月31日止。租赁期满，如需续租，应提前30天协商。

第三条 租金及支付方式
月租金为人民币5500元整（¥5,500），每月1日前支付当月租金。首次支付时需一次性支付首月租金及两个月押金，共计人民币16500元整。

第四条 房屋用途
乙方承诺仅将房屋用于居住，不得擅自改变房屋用途，不得进行违法活动。

第五条 房屋维修
房屋正常维修由甲方负责，因乙方使用不当造成的损坏由乙方承担维修费用。

第六条 违约责任
如甲方违约，应承担相应的法律责任并赔偿乙方损失；如乙方违约，甲方有权没收押金并要求乙方立即搬离。

第七条 其他约定
1. 乙方不得私自转租房屋
2. 水电燃气费用由乙方承担
3. 物业费由甲方承担
4. 房屋内不得饲养宠物

第八条 保险条款
甲方应为房屋购买财产保险，乙方应为个人财物购买相应保险，双方各自承担保险费用。

第九条 争议解决
本合同履行过程中发生争议，双方应协商解决；协商不成的，可向房屋所在地人民法院起诉。

第十条 合同生效
本合同一式两份，甲乙双方各执一份，自双方签字之日起生效。

甲方签字：_________________ 日期：2024年2月25日

乙方签字：_________________ 日期：2024年2月25日`;

  const [contractA, setContractA] = useState<string>(defaultContractA);
  const [contractB, setContractB] = useState<string>(defaultContractB);
  const [differences, setDifferences] = useState<Difference[]>([]);
  const [isComparing, setIsComparing] = useState(false);
  const [selectedDiff, setSelectedDiff] = useState<string | null>(null);
  
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  // 模拟文件上传
  const handleFileUpload = (type: 'A' | 'B', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (type === 'A') {
          setContractA(content);
        } else {
          setContractB(content);
        }
      };
      reader.readAsText(file);
    }
  };

  // 模拟合同比对
  const handleCompare = () => {
    if (!contractA || !contractB) return;
    
    setIsComparing(true);
    
    // 模拟比对结果
    setTimeout(() => {
      const mockDifferences: Difference[] = [
        {
          id: '1',
          type: 'semantic',
          summary: '法律依据更新：合同法 → 民法典',
          position: 1,
          leftText: '根据《中华人民共和国合同法》及相关法律法规',
          rightText: '根据《中华人民共和国民法典》及相关法律法规',
          leftHighlight: '合同法',
          rightHighlight: '民法典',
          status: 'pending'
        },
        {
          id: '2',
          type: 'critical',
          summary: '租赁期限变更：12个月 → 18个月',
          position: 2,
          leftText: '租赁期限为12个月，自2024年3月1日起至2025年2月28日止',
          rightText: '租赁期限为18个月，自2024年3月1日起至2025年8月31日止',
          leftHighlight: '12个月',
          rightHighlight: '18个月',
          status: 'pending'
        },
        {
          id: '3',
          type: 'critical',
          summary: '月租金调整：5000元 → 5500元',
          position: 3,
          leftText: '月租金为人民币5000元整（¥5,000）',
          rightText: '月租金为人民币5500元整（¥5,500）',
          leftHighlight: '5000元整',
          rightHighlight: '5500元整',
          status: 'pending'
        },
        {
          id: '4',
          type: 'critical',
          summary: '押金总额变更：15000元 → 16500元',
          position: 4,
          leftText: '共计人民币15000元整',
          rightText: '共计人民币16500元整',
          leftHighlight: '15000元整',
          rightHighlight: '16500元整',
          status: 'pending'
        },
        {
          id: '5',
          type: 'semantic',
          summary: '违约责任表述优化',
          position: 5,
          leftText: '如甲方违约，应双倍返还押金',
          rightText: '如甲方违约，应承担相应的法律责任并赔偿乙方损失',
          leftHighlight: '双倍返还押金',
          rightHighlight: '承担相应的法律责任并赔偿乙方损失',
          status: 'pending'
        },
        {
          id: '6',
          type: 'addition',
          summary: '新增宠物饲养限制条款',
          position: 6,
          leftText: '',
          rightText: '4. 房屋内不得饲养宠物',
          leftHighlight: '',
          rightHighlight: '房屋内不得饲养宠物',
          status: 'pending'
        },
        {
          id: '7',
          type: 'addition',
          summary: '新增保险条款',
          position: 7,
          leftText: '',
          rightText: '第八条 保险条款\n甲方应为房屋购买财产保险，乙方应为个人财物购买相应保险，双方各自承担保险费用。',
          leftHighlight: '',
          rightHighlight: '保险条款',
          status: 'pending'
        },
        {
          id: '8',
          type: 'format',
          summary: '条款编号调整：第九条 → 第十条',
          position: 8,
          leftText: '第九条 合同生效',
          rightText: '第十条 合同生效',
          leftHighlight: '第九条',
          rightHighlight: '第十条',
          status: 'pending'
        },
        {
          id: '9',
          type: 'format',
          summary: '签字日期格式调整',
          position: 9,
          leftText: '甲方签字：_________________ 日期：2024年2月25日\n乙方签字：_________________ 日期：2024年2月25日',
          rightText: '甲方签字：_________________ 日期：2024年2月25日\n\n乙方签字：_________________ 日期：2024年2月25日',
          leftHighlight: '甲方签字：_________________ 日期：2024年2月25日\n乙方签字：',
          rightHighlight: '甲方签字：_________________ 日期：2024年2月25日\n\n乙方签字：',
          status: 'pending'
        }
      ];
      
      setDifferences(mockDifferences);
      setIsComparing(false);
    }, 2000);
  };

  // 跳转到差异位置
  const scrollToDifference = (diffId: string) => {
    setSelectedDiff(diffId);
    
    const diff = differences.find(d => d.id === diffId);
    if (!diff) return;

    // 模拟滚动到对应位置
    if (leftPanelRef.current && rightPanelRef.current) {
      // 这里可以根据实际需要计算精确的滚动位置
      // 目前使用简单的模拟滚动
      const scrollPosition = diff.position * 100; // 简单的位置计算
      
      leftPanelRef.current.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
      
      rightPanelRef.current.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  // 处理差异决策
  const handleDifferenceDecision = (diffId: string, decision: 'accepted' | 'rejected') => {
    setDifferences(prev => 
      prev.map(diff => 
        diff.id === diffId ? { ...diff, status: decision } : diff
      )
    );
  };

  // 获取差异类型样式
  const getDifferenceTypeStyle = (type: string) => {
    switch (type) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'semantic':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'format':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'addition':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // 获取差异类型标签
  const getDifferenceTypeLabel = (type: string) => {
    switch (type) {
      case 'critical':
        return '关键差异';
      case 'semantic':
        return '语义差异';
      case 'format':
        return '格式差异';
      case 'addition':
        return '新增/删除';
      default:
        return '其他差异';
    }
  };

  // 高亮文本函数
  const highlightText = (text: string, highlight: string, isLeft: boolean = false) => {
    if (!highlight || !text.includes(highlight)) {
      return text;
    }

    const parts = text.split(highlight);
    const highlightClass = isLeft ? 'bg-yellow-200' : '';
    
    return parts.reduce((acc, part, index) => {
      if (index === 0) return part;
      return acc + `<span class="${highlightClass}">${highlight}</span>` + part;
    }, parts[0]);
  };

  // 渲染带高亮的合同文本
  const renderContractText = (text: string, isRight: boolean = false) => {
    if (!text) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-400">
          <div className="text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>请上传合同文件</p>
          </div>
        </div>
      );
    }

    const lines = text.split('\n');
    const selectedDifference = differences.find(d => d.id === selectedDiff);
    
    return (
      <div className="whitespace-pre-wrap leading-relaxed text-sm">
        {lines.map((line, index) => {
          let className = '';
          let processedLine = line;
          
          // 如果有选中的差异，进行高亮处理
          if (selectedDifference) {
            const highlightText = isRight ? selectedDifference.rightHighlight : selectedDifference.leftHighlight;
            if (highlightText && line.includes(highlightText)) {
              if (isRight) {
                // 右侧使用原有的颜色标识
                if (selectedDifference.type === 'critical') {
                  className = 'bg-red-100 border-l-4 border-red-400 pl-2 py-1';
                } else if (selectedDifference.type === 'semantic') {
                  className = 'bg-green-100 border-l-4 border-green-400 pl-2 py-1';
                } else if (selectedDifference.type === 'addition') {
                  className = 'bg-blue-100 border-l-4 border-blue-400 pl-2 py-1';
                } else if (selectedDifference.type === 'format') {
                  className = 'bg-gray-100 border-l-4 border-gray-400 pl-2 py-1';
                }
              } else {
                // 左侧使用黄色背景
                className = 'bg-yellow-200 border-l-4 border-yellow-400 pl-2 py-1';
              }
            }
          } else if (isRight && differences.length > 0) {
            // 默认的右侧高亮逻辑（当没有选中特定差异时）
            if (line.includes('民法典')) {
              className = 'bg-green-100 border-l-4 border-green-400 pl-2 py-1';
            } else if (line.includes('18个月') || line.includes('2025年8月31日')) {
              className = 'bg-red-100 border-l-4 border-red-400 pl-2 py-1';
            } else if (line.includes('5500元整') || line.includes('¥5,500')) {
              className = 'bg-red-100 border-l-4 border-red-400 pl-2 py-1';
            } else if (line.includes('16500元整')) {
              className = 'bg-red-100 border-l-4 border-red-400 pl-2 py-1';
            } else if (line.includes('承担相应的法律责任并赔偿乙方损失')) {
              className = 'bg-green-100 border-l-4 border-green-400 pl-2 py-1';
            } else if (line.includes('房屋内不得饲养宠物')) {
              className = 'bg-blue-100 border-l-4 border-blue-400 pl-2 py-1';
            } else if (line.includes('第八条 保险条款') || line.includes('甲方应为房屋购买财产保险')) {
              className = 'bg-blue-100 border-l-4 border-blue-400 pl-2 py-1';
            } else if (line.includes('第十条 合同生效')) {
              className = 'bg-gray-100 border-l-4 border-gray-400 pl-2 py-1';
            } else if (line === '' && index > 0 && lines[index-1].includes('甲方签字')) {
              className = 'bg-gray-100 border-l-4 border-gray-400 pl-2 py-1';
            }
          }
          
          return (
            <div key={index} className={className}>
              {processedLine || '\u00A0'}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部操作区 */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900">合同比对工具</h1>
          
          <div className="flex items-center space-x-4">
            {/* 上传合同A */}
            <label className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors whitespace-nowrap">
              <Upload className="w-4 h-4 mr-2" />
              上传合同 A
              <input
                type="file"
                accept=".txt,.doc,.docx,.pdf"
                onChange={(e) => handleFileUpload('A', e)}
                className="hidden"
              />
            </label>

            {/* 上传合同B */}
            <label className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer transition-colors whitespace-nowrap">
              <Upload className="w-4 h-4 mr-2" />
              上传合同 B
              <input
                type="file"
                accept=".txt,.doc,.docx,.pdf"
                onChange={(e) => handleFileUpload('B', e)}
                className="hidden"
              />
            </label>

            {/* 开始比对按钮 */}
            <button
              onClick={handleCompare}
              disabled={!contractA || !contractB || isComparing}
              className="flex items-center px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              <Play className="w-4 h-4 mr-2" />
              {isComparing ? '比对中...' : '开始比对'}
            </button>
          </div>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="flex h-[calc(100vh-80px)]">
        {/* 左侧：合同A */}
        <div className="w-1/3 bg-white border-r border-gray-200">
          <div className="p-4 border-b border-gray-200 bg-blue-50">
            <h2 className="font-semibold text-gray-900 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              合同 A（被比对文档）
            </h2>
          </div>
          <div 
            ref={leftPanelRef}
            className="p-6 h-full overflow-y-auto"
          >
            {renderContractText(contractA)}
          </div>
        </div>

        {/* 中间：差异导航 */}
        <div className="w-1/3 bg-gray-50 border-r border-gray-200">
          <div className="p-4 border-b border-gray-200 bg-gray-100">
            <h2 className="font-semibold text-gray-900">
              差异概览 ({differences.length})
            </h2>
          </div>
          <div className="h-full overflow-y-auto">
            {differences.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>暂无差异数据</p>
                  <p className="text-sm">请点击"开始比对"查看示例</p>
                </div>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {differences.map((diff) => (
                  <div
                    key={diff.id}
                    className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      selectedDiff === diff.id ? 'ring-2 ring-blue-500' : ''
                    } ${
                      diff.status === 'accepted' ? 'bg-green-50 border-green-200' :
                      diff.status === 'rejected' ? 'bg-red-50 border-red-200' :
                      'bg-white border-gray-200'
                    }`}
                    onClick={() => scrollToDifference(diff.id)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className={`px-2 py-1 text-xs rounded-full border ${getDifferenceTypeStyle(diff.type)}`}>
                        {getDifferenceTypeLabel(diff.type)}
                      </span>
                      {diff.status === 'pending' && (
                        <div className="flex space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDifferenceDecision(diff.id, 'accepted');
                            }}
                            className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
                            title="接受修改"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDifferenceDecision(diff.id, 'rejected');
                            }}
                            className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                            title="拒绝修改"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <p className="font-medium text-gray-900 mb-2 text-sm">{diff.summary}</p>
                    
                    {diff.leftText && (
                      <div className="text-xs text-gray-600 mb-1">
                        <span className="font-medium">原文：</span>
                        <span className="bg-red-50 px-1 rounded">{diff.leftText}</span>
                      </div>
                    )}
                    
                    {diff.rightText && (
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">修改：</span>
                        <span className="bg-green-50 px-1 rounded">{diff.rightText}</span>
                      </div>
                    )}

                    {diff.status !== 'pending' && (
                      <div className="mt-2 text-xs text-gray-500">
                        状态：{diff.status === 'accepted' ? '已接受' : '已拒绝'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 右侧：合同B */}
        <div className="w-1/3 bg-white">
          <div className="p-4 border-b border-gray-200 bg-green-50">
            <h2 className="font-semibold text-gray-900 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-green-600" />
              合同 B（比对文档）
            </h2>
          </div>
          <div 
            ref={rightPanelRef}
            className="p-6 h-full overflow-y-auto"
          >
            {renderContractText(contractB, true)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractComparePage;
